import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, withKeys as _withKeys, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "cohort-fields" };
const _hoisted_2 = { class: "d-flex align-items-baseline justify-content-between" };
const _hoisted_3 = { class: "text-muted" };
const _hoisted_4 = {
    key: 0,
    class: "list-unstyled mb-2"
};
const _hoisted_5 = {
    key: 1,
    class: "cohort-fields__empty text-center text-muted mb-3"
};
const _hoisted_6 = ["title", "aria-expanded", "onClick"];
const _hoisted_7 = ["value", "aria-label", "onInput"];
const _hoisted_8 = ["onClick"];
const _hoisted_9 = { class: "text-muted ml-2" };
const _hoisted_10 = { class: "custom-control custom-checkbox mr-3 text-nowrap" };
const _hoisted_11 = ["id", "checked", "onChange"];
const _hoisted_12 = ["for"];
const _hoisted_13 = {
    class: "btn-group btn-group-sm flex-shrink-0",
    role: "group",
    "aria-label": "Reorder cohort field"
};
const _hoisted_14 = ["disabled", "onClick"];
const _hoisted_15 = ["disabled", "onClick"];
const _hoisted_16 = ["title", "aria-label", "onClick"];
const _hoisted_17 = {
    key: 0,
    class: "card-body py-3"
};
const _hoisted_18 = { class: "d-flex align-items-baseline justify-content-between mb-2" };
const _hoisted_19 = ["onClick"];
const _hoisted_20 = { key: 0 };
const _hoisted_21 = ["onUpdate:modelValue"];
const _hoisted_22 = { class: "d-flex align-items-center mt-2" };
const _hoisted_23 = ["disabled", "onClick"];
const _hoisted_24 = { key: 1 };
const _hoisted_25 = {
    key: 0,
    class: "form-text text-danger mb-2"
};
const _hoisted_26 = { class: "form-row" };
const _hoisted_27 = { class: "text-muted mr-1 cohort-fields__number" };
const _hoisted_28 = ["value", "aria-label", "onInput"];
const _hoisted_29 = ["disabled", "onClick"];
const _hoisted_30 = { class: "col-lg-4 col-md-6 mb-2 d-flex align-items-center" };
const _hoisted_31 = { class: "text-muted mr-1 cohort-fields__number" };
const _hoisted_32 = ["onUpdate:modelValue", "onKeydown", "onBlur"];
const _hoisted_33 = { class: "d-flex align-items-center" };
const _hoisted_34 = ["disabled"];
import { v4 as uuid4 } from 'uuid';
import { computed, nextTick, reactive, ref } from 'vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'CohortFieldsEditor',
    props: {
        modelValue: { default: () => [] },
        errors: { default: () => [] }
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // Keyed by field id so it never reaches the serialized cohort_fields payload.
        const drafts = reactive({});
        const bulkOpen = reactive({});
        const bulkText = reactive({});
        const expandedId = ref(null);
        const nameInputs = reactive({});
        const fields = computed(() => props.modelValue);
        const summary = computed(() => {
            if (!fields.value.length) {
                return '';
            }
            const ready = fields.value.filter(field => field.options.length > 0).length;
            return `${fields.value.length} ${fields.value.length === 1 ? 'field' : 'fields'} · ${ready} ready`;
        });
        const emptyField = computed(() => fields.value.find(field => !field.label.trim() && !field.options.length));
        const revealEmptyField = () => {
            expandedId.value = emptyField.value.id;
            nextTick(() => nameInputs[emptyField.value.id]?.focus());
        };
        const isExpanded = (field) => expandedId.value === field.id;
        const registerNameInput = (field, el) => {
            nameInputs[field.id] = el;
        };
        const commit = (next) => emit('update:modelValue', next);
        const update = (field, changes) => {
            commit(fields.value.map(item => (item.id === field.id ? { ...item, ...changes } : item)));
        };
        const addField = () => {
            const field = { id: uuid4(), label: '', required: false, options: [] };
            commit([...fields.value, field]);
            expandedId.value = field.id;
            nextTick(() => nameInputs[field.id]?.focus());
        };
        const removeField = (field) => {
            commit(fields.value.filter(item => item.id !== field.id));
        };
        const move = (index, direction) => {
            const target = index + direction;
            if (target < 0 || target >= fields.value.length) {
                return;
            }
            const next = [...fields.value];
            next.splice(target, 0, next.splice(index, 1)[0]);
            commit(next);
        };
        const toggle = (field) => {
            expandedId.value = isExpanded(field) ? null : field.id;
        };
        const rename = (field, label) => update(field, { label });
        const answerAriaLabel = (field, index) => `Answer ${index + 1} of ${field.label.trim() || 'cohort field'}`;
        const answerSummary = (field) => {
            const labels = field.options.map(option => option.label.trim() || 'Untitled');
            if (!labels.length) {
                return 'no answers yet';
            }
            const shown = labels.slice(0, 3).join(', ');
            return labels.length > 3 ? `${shown} +${labels.length - 3} more` : shown;
        };
        const renameOption = (field, option, label) => {
            update(field, {
                options: field.options.map(item => (item.id === option.id ? { ...item, label } : item)),
            });
        };
        const removeOption = (field, option) => {
            update(field, { options: field.options.filter(item => item.id !== option.id) });
        };
        const commitDraft = (field) => {
            const label = (drafts[field.id] || '').trim();
            if (!label) {
                return;
            }
            drafts[field.id] = '';
            update(field, { options: [...field.options, { id: uuid4(), label }] });
        };
        const toggleBulk = (field) => {
            const opening = !bulkOpen[field.id];
            if (opening) {
                bulkText[field.id] = field.options.map(option => option.label).join('\n');
            }
            bulkOpen[field.id] = opening;
        };
        const bulkLines = (field) => (bulkText[field.id] || '')
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);
        const bulkLineCount = (field) => bulkLines(field).length;
        const applyBulk = (field) => {
            const lines = bulkLines(field);
            if (!lines.length) {
                return;
            }
            // Match by label so an answer that's still present after the paste keeps its id (stable Vue :key).
            const reusable = [...field.options];
            const options = lines.map((label) => {
                const match = reusable.findIndex(option => option.label.trim() === label);
                if (match !== -1) {
                    return { ...reusable.splice(match, 1)[0], label };
                }
                return { id: uuid4(), label };
            });
            bulkText[field.id] = '';
            bulkOpen[field.id] = false;
            update(field, { options });
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _cache[0] || (_cache[0] = _createElementVNode("label", { class: "control-label mb-0" }, "Cohort Fields", -1)),
                    _createElementVNode("small", _hoisted_3, _toDisplayString(summary.value), 1)
                ]),
                _cache[11] || (_cache[11] = _createElementVNode("small", { class: "form-text text-muted mb-2" }, " Questions learners answer once, on the Team Data Entry screen. Reports group results by the answers. ", -1)),
                (__props.errors.length)
                    ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.errors, (message, index) => {
                            return (_openBlock(), _createElementBlock("li", {
                                key: index,
                                class: "form-text text-danger mt-0"
                            }, _toDisplayString(message), 1));
                        }), 128))
                    ]))
                    : _createCommentVNode("", true),
                (!fields.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[1] || (_cache[1] = [
                            _createElementVNode("div", { class: "font-weight-bold text-body" }, "No cohort fields yet", -1),
                            _createElementVNode("small", null, "Learners will only be asked for a team name.", -1)
                        ]))]))
                    : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fields.value, (field, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                        key: field.id,
                        class: _normalizeClass(["card mb-2", { 'cohort-fields__card--open': isExpanded(field) }])
                    }, [
                        _createElementVNode("div", {
                            class: _normalizeClass(["card-header d-flex align-items-center py-2", isExpanded(field) ? 'bg-white' : 'bg-light'])
                        }, [
                            _createElementVNode("button", {
                                type: "button",
                                class: "btn btn-link btn-sm text-muted px-0 mr-2",
                                title: isExpanded(field) ? 'Collapse' : 'Expand',
                                "aria-expanded": isExpanded(field),
                                onClick: ($event) => (toggle(field))
                            }, [
                                _createElementVNode("i", {
                                    class: _normalizeClass(["fas fa-chevron-right cohort-fields__caret", { 'cohort-fields__caret--open': isExpanded(field) }])
                                }, null, 2)
                            ], 8, _hoisted_6),
                            (isExpanded(field))
                                ? (_openBlock(), _createElementBlock("input", {
                                    key: 0,
                                    ref_for: true,
                                    ref: el => registerNameInput(field, el),
                                    type: "text",
                                    class: "form-control font-weight-bold mr-3",
                                    value: field.label,
                                    "aria-label": 'Name of cohort field ' + (index + 1),
                                    placeholder: "Field name, e.g. Region",
                                    onInput: ($event) => (rename(field, $event.target.value))
                                }, null, 40, _hoisted_7))
                                : (_openBlock(), _createElementBlock("button", {
                                    key: 1,
                                    type: "button",
                                    class: "btn btn-link text-left flex-grow-1 text-decoration-none px-0 mr-3",
                                    onClick: ($event) => (toggle(field))
                                }, [
                                    _createElementVNode("span", {
                                        class: _normalizeClass(["font-weight-bold", field.label.trim() ? 'text-body' : 'text-muted'])
                                    }, _toDisplayString(field.label.trim() || 'Untitled field'), 3),
                                    _createElementVNode("small", _hoisted_9, _toDisplayString(answerSummary(field)), 1)
                                ], 8, _hoisted_8)),
                            _createElementVNode("div", _hoisted_10, [
                                _createElementVNode("input", {
                                    id: 'required-' + field.id,
                                    type: "checkbox",
                                    class: "custom-control-input",
                                    checked: field.required,
                                    onChange: ($event) => (update(field, { required: $event.target.checked }))
                                }, null, 40, _hoisted_11),
                                _createElementVNode("label", {
                                    class: "custom-control-label font-weight-normal",
                                    for: 'required-' + field.id
                                }, "Required", 8, _hoisted_12)
                            ]),
                            _createElementVNode("div", _hoisted_13, [
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "btn btn-light border",
                                    disabled: index === 0,
                                    title: "Move up",
                                    "aria-label": "Move up",
                                    onClick: ($event) => (move(index, -1))
                                }, [...(_cache[2] || (_cache[2] = [
                                        _createElementVNode("i", { class: "fas fa-arrow-up" }, null, -1)
                                    ]))], 8, _hoisted_14),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "btn btn-light border",
                                    disabled: index === fields.value.length - 1,
                                    title: "Move down",
                                    "aria-label": "Move down",
                                    onClick: ($event) => (move(index, 1))
                                }, [...(_cache[3] || (_cache[3] = [
                                        _createElementVNode("i", { class: "fas fa-arrow-down" }, null, -1)
                                    ]))], 8, _hoisted_15),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "btn btn-light border text-danger",
                                    title: 'Remove ' + (field.label.trim() || 'cohort field ' + (index + 1)),
                                    "aria-label": 'Remove cohort field ' + (index + 1),
                                    onClick: ($event) => (removeField(field))
                                }, [...(_cache[4] || (_cache[4] = [
                                        _createElementVNode("i", { class: "fa fa-trash" }, null, -1)
                                    ]))], 8, _hoisted_16)
                            ])
                        ], 2),
                        (isExpanded(field))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                _createElementVNode("div", _hoisted_18, [
                                    _cache[5] || (_cache[5] = _createElementVNode("small", { class: "text-muted text-uppercase font-weight-bold" }, "Answers", -1)),
                                    _createElementVNode("button", {
                                        type: "button",
                                        class: "btn btn-link btn-sm p-0",
                                        onClick: ($event) => (toggleBulk(field))
                                    }, _toDisplayString(bulkOpen[field.id] ? 'Back to list' : 'Paste a list'), 9, _hoisted_19)
                                ]),
                                (bulkOpen[field.id])
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                        _withDirectives(_createElementVNode("textarea", {
                                            "onUpdate:modelValue": ($event) => ((bulkText[field.id]) = $event),
                                            rows: "5",
                                            class: "form-control",
                                            "aria-label": "One answer per line",
                                            placeholder: "North America\nEMEA\nAPAC"
                                        }, null, 8, _hoisted_21), [
                                            [_vModelText, bulkText[field.id]]
                                        ]),
                                        _createElementVNode("div", _hoisted_22, [
                                            _createElementVNode("button", {
                                                type: "button",
                                                class: "btn btn-secondary btn-sm mr-2",
                                                disabled: !bulkLineCount(field),
                                                onClick: ($event) => (applyBulk(field))
                                            }, " Save " + _toDisplayString(bulkLineCount(field)) + " " + _toDisplayString(bulkLineCount(field) === 1 ? 'answer' : 'answers'), 9, _hoisted_23),
                                            _cache[6] || (_cache[6] = _createElementVNode("small", { class: "text-muted" }, "One answer per line. Deleting a line removes that answer.", -1))
                                        ])
                                    ]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                        (!field.options.length)
                                            ? (_openBlock(), _createElementBlock("small", _hoisted_25, " Add at least one answer — the field cannot be saved without one. "))
                                            : _createCommentVNode("", true),
                                        _createElementVNode("div", _hoisted_26, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.options, (option, oIndex) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                    key: option.id,
                                                    class: "col-lg-4 col-md-6 mb-2 d-flex align-items-center"
                                                }, [
                                                    _createElementVNode("small", _hoisted_27, _toDisplayString(oIndex + 1), 1),
                                                    _createElementVNode("input", {
                                                        type: "text",
                                                        class: "form-control form-control-sm",
                                                        value: option.label,
                                                        "aria-label": answerAriaLabel(field, oIndex),
                                                        placeholder: "Answer",
                                                        onInput: ($event) => (renameOption(field, option, $event.target.value))
                                                    }, null, 40, _hoisted_28),
                                                    _createElementVNode("button", {
                                                        type: "button",
                                                        class: "btn btn-sm btn-link text-muted px-1",
                                                        disabled: field.options.length <= 1,
                                                        title: "Remove answer",
                                                        "aria-label": "Remove answer",
                                                        onClick: ($event) => (removeOption(field, option))
                                                    }, [...(_cache[7] || (_cache[7] = [
                                                            _createElementVNode("i", { class: "fa fa-times" }, null, -1)
                                                        ]))], 8, _hoisted_29)
                                                ]));
                                            }), 128)),
                                            _createElementVNode("div", _hoisted_30, [
                                                _createElementVNode("small", _hoisted_31, _toDisplayString(field.options.length + 1), 1),
                                                _withDirectives(_createElementVNode("input", {
                                                    type: "text",
                                                    class: "form-control form-control-sm cohort-fields__draft",
                                                    "onUpdate:modelValue": ($event) => ((drafts[field.id]) = $event),
                                                    "aria-label": "New answer",
                                                    placeholder: "Type an answer, press Enter",
                                                    onKeydown: _withKeys(_withModifiers(($event) => (commitDraft(field)), ["prevent"]), ["enter"]),
                                                    onBlur: ($event) => (commitDraft(field))
                                                }, null, 40, _hoisted_32), [
                                                    [_vModelText, drafts[field.id]]
                                                ]),
                                                _cache[8] || (_cache[8] = _createElementVNode("span", { class: "cohort-fields__number" }, null, -1))
                                            ])
                                        ]),
                                        _cache[9] || (_cache[9] = _createElementVNode("small", { class: "text-muted" }, " Enter keeps adding. Reports group learners by these answers, so keep them short. ", -1))
                                    ]))
                            ]))
                            : _createCommentVNode("", true)
                    ], 2));
                }), 128)),
                _createElementVNode("div", _hoisted_33, [
                    _createElementVNode("button", {
                        type: "button",
                        class: "btn btn-secondary btn-sm",
                        disabled: !!emptyField.value,
                        onClick: addField
                    }, [...(_cache[10] || (_cache[10] = [
                            _createElementVNode("i", { class: "fa fa-plus" }, null, -1),
                            _createTextVNode(" Add Cohort Field ", -1)
                        ]))], 8, _hoisted_34),
                    (emptyField.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            type: "button",
                            class: "btn btn-link btn-sm text-muted",
                            onClick: revealEmptyField
                        }, " Give the empty field a name and answers first. "))
                        : _createCommentVNode("", true)
                ])
            ]));
        };
    }
});
